import { approveRemainingActionPlan } from '../api/chat.client.js';
import { taskCorrectionForMessage } from '../../../agents/planner/task-correction-binding.js';
import { batchCorrectionForMessage } from '../../../agents/planner/batch-item-correction.js';
import { compoundSelectionForMessage } from '../../../agents/planner/compound-selection.contract.js';
import { browserChatTimeZone } from '../../../shared/chat-time-zone.js';
import {
  commandLabel,
  commandContextRefCount,
  commandClarificationKey,
  uiCommandSchema,
  type UiCommandRequest,
} from '../../../agents/context/ui-command-registry.js';
import { i18n } from '../../../i18n/index.js';
import { message } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ApiError } from '../../../shared/api-client/index.js';
import {
  decideApproval,
  isApprovalPresentationV1,
  type ApprovalItem,
} from '../../approvals/api/approvals.client.js';
import {
  cancelChatTurn,
  getChatConversation,
  postChatMessageStream,
  requestActionPlanItemUndo,
  requestActionPlanItemRetry,
  type ChatStreamCursor,
  type ChatStreamEvent,
  type ChatConversationResponse,
  type ChatHistoryOptions,
  type PostChatMessageInput,
} from '../api/chat.client.js';
import { isReasoningEnabled } from '../lib/reasoningMode.js';
import { enqueueChatClientAction } from '../lib/chatClientActionBus.js';
import type { ChatClientActionScopeIdentity } from '../lib/chatClientActionBus.js';
import type { OpenRouterChatModelId } from '@govard/llm-gate-client';
import type { LlmGatewayProfile } from '../api/chat.client.js';

import { createAiChatEventReplayGuard, sanitizeAiChatClientAction } from '@govard/ai-chat-kit';
import type {
  AiChatMessageSnapshot,
  ChatContextRef,
  MeetingSnapshotItem,
  Message,
  SendMessageOptions,
  StructuredMessagePayload,
  ToolCall,
} from '@govard/ai-chat-kit';
import type { UseAgentReturn } from '@govard/ai-chat-kit/react';
import type { Scope } from '@govard/tenant-core';

export type EntityChatContextType = 'deal' | 'contact' | 'project';
export type ChatAgentContextType = EntityChatContextType | 'none';

/** UI message enriched with the agent that produced its content. */
export interface ChatUiMessage extends Message {
  /** Id of the agent that executed the tool call (e.g. a delegated specialist). */
  agentId?: string;
  uiCommand?: UiCommandRequest;
}

export interface UseEntityChatInput {
  contextEntityType: ChatAgentContextType;
  contextEntityId: string;
  conversationId?: string | null;
  gatewayProfile?: LlmGatewayProfile;
  openRouterModel?: OpenRouterChatModelId;
}

export interface EntityChatSendOptions extends SendMessageOptions {
  openRouterModel?: OpenRouterChatModelId;
  meetingSnapshot?: MeetingSnapshotItem[];
  /** PR 5: conversation-owned attachment id attached to this outgoing message. */
  attachmentId?: string;
}

export interface EntityChatAgent extends Omit<UseAgentReturn, 'send' | 'pendingApprovals'> {
  pendingApprovals: Array<
    UseAgentReturn['pendingApprovals'][number] & { presentation?: ApprovalItem['presentation'] }
  >;
  /** Server-verified entity currently active in this conversation. */
  selectedEntity: NonNullable<ChatConversationResponse['selectedEntity']> | null;
  /** Server-resolved single deal currently selected by the conversation. */
  selectedDeal: { id: string; label: string } | null;
  send(userInput: string | UiCommandRequest, options?: EntityChatSendOptions): Promise<void>;
  /** False while the workspace scope is still resolving: sends would no-op. */
  scopeReady: boolean;
  approve(approvalId: string, decision: 'approve' | 'reject'): Promise<void>;
  /**
   * Sync a decision made outside the hook (e.g. AiApprovalCard runs its own
   * mutation) back into the messages stream and the pending-approvals panel.
   */
  applyApprovalDecision(approval: ApprovalItem): void;
  decideActionPlanItem(input: {
    planId: string;
    itemId: string;
    approvalId: string;
    decision: 'approve' | 'reject';
    remainingToken?: string;
  }): Promise<void>;
  requestActionPlanRetry(input: {
    planId: string;
    itemId: string;
    failedExecutionId: string;
  }): Promise<void>;
  requestActionPlanUndo(input: { planId: string; itemId: string }): Promise<void>;
  cancelStream(): void;
  retryLastRequest(): Promise<void>;
  canRetryLastRequest: boolean;
  hasEarlierMessages: boolean;
  isLoadingEarlier: boolean;
  earlierHistoryError: boolean;
  loadEarlier(beforePrepend?: () => void): Promise<void>;
}

interface ActiveDurableChatTurn {
  version: 1;
  workspaceId: string;
  userId: string;
  conversationId: string;
  request: PostChatMessageInput;
  runId: string;
  turnId: string;
  cursor: ChatStreamCursor | null;
  /**
   * Stream output already rendered for this turn. The server replays only
   * events after the stored cursor, so this draft is what restores the visible
   * part of an answer after the hook lost its state.
   */
  draft?: {
    content: string;
    reasoning?: string;
    toolCalls?: ToolCall[];
  };
}

const ACTIVE_DURABLE_CHAT_TURN_KEY_PREFIX = 'govard.crm.ai-chat.active-turn.v1';
const MAX_STREAM_RECONNECTS = 2;
const STREAM_RECONNECT_DELAYS_MS = [50, 150] as const;

/**
 * Live plan sync cadence. Approvals and executions can finish outside this
 * client (another tab, Telegram, the approvals screen) while the card stays
 * open, and the chat has no push channel for plan progress — so an open
 * non-terminal plan re-reads the conversation on this interval. Read-only.
 */
const PLAN_LIVE_POLL_MS = 15_000;

/** Plan item statuses that can still move without a new chat turn. Terminal
 * error states (`failed`, `stale`, `rejected`, `cancelled`) and final states
 * (`succeeded`, `undone`) never change on their own, so polling them would
 * re-read forever. */
const PLAN_LIVE_STATUSES: ReadonlySet<string> = new Set([
  'blocked',
  'pending',
  'approved',
  'executing',
  'undo_pending',
]);

function messageHasLivePlan(message: ChatUiMessage): boolean {
  const payload = message.structuredPayload;
  if (payload?.type !== 'crm_action_plan_v1') return false;
  const hasFailedItem = payload.plan.items.some((item) => item.status === 'failed');
  return payload.plan.items.some(
    (item) => PLAN_LIVE_STATUSES.has(item.status) && !(hasFailedItem && item.status === 'blocked'),
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function parseDurableDraft(value: unknown): ActiveDurableChatTurn['draft'] {
  if (!isRecord(value) || typeof value.content !== 'string') return undefined;
  const draft: NonNullable<ActiveDurableChatTurn['draft']> = { content: value.content };
  if (typeof value.reasoning === 'string') {
    draft.reasoning = value.reasoning;
  }
  if (Array.isArray(value.toolCalls)) {
    const toolCalls = value.toolCalls.filter(
      (call): call is ToolCall =>
        isRecord(call) && typeof call.id === 'string' && typeof call.name === 'string',
    );
    if (toolCalls.length > 0) {
      draft.toolCalls = toolCalls;
    }
  }
  return draft;
}

function normalizeChatErrorMessage(value: unknown, fallback: string): string {
  if (typeof value === 'string' && value.trim() !== '') return value;
  if (isRecord(value)) {
    for (const key of ['message', 'error', 'detail']) {
      const nested = value[key];
      if (typeof nested === 'string' && nested.trim() !== '') return nested;
    }
  }
  return fallback;
}

function getSessionStorage(): Storage | null {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage;
  } catch {
    return null;
  }
}

function getActiveDurableChatTurnKey(
  workspaceId: string,
  userId: string,
  conversationId: string,
): string {
  return `${ACTIVE_DURABLE_CHAT_TURN_KEY_PREFIX}:${[workspaceId, userId, conversationId]
    .map((part) => encodeURIComponent(part))
    .join(':')}`;
}

function readActiveDurableChatTurn(
  scope: Scope,
  conversationId: string,
): ActiveDurableChatTurn | null {
  const storage = getSessionStorage();
  if (storage === null) return null;
  const storageKey = getActiveDurableChatTurnKey(scope.workspaceId, scope.userId, conversationId);

  try {
    const raw = storage.getItem(storageKey);
    if (raw === null) return null;
    const value: unknown = JSON.parse(raw);
    if (
      !isRecord(value) ||
      value.version !== 1 ||
      value.workspaceId !== scope.workspaceId ||
      value.userId !== scope.userId ||
      value.conversationId !== conversationId ||
      typeof value.runId !== 'string' ||
      typeof value.turnId !== 'string' ||
      !isRecord(value.request) ||
      (typeof value.request.message !== 'string' &&
        !uiCommandSchema.safeParse({
          commandId: value.request.commandId,
          params: value.request.params,
        }).success) ||
      (value.request.contextEntityType !== 'deal' &&
        value.request.contextEntityType !== 'contact' &&
        value.request.contextEntityType !== 'project' &&
        value.request.contextEntityType !== 'none') ||
      typeof value.request.contextEntityId !== 'string' ||
      value.request.runId !== value.runId ||
      value.request.turnId !== value.turnId ||
      (value.cursor !== null &&
        (!isRecord(value.cursor) ||
          typeof value.cursor.eventId !== 'string' ||
          typeof value.cursor.runId !== 'string' ||
          typeof value.cursor.turnId !== 'string' ||
          typeof value.cursor.sequence !== 'number' ||
          value.cursor.runId !== value.runId ||
          value.cursor.turnId !== value.turnId))
    ) {
      storage.removeItem(storageKey);
      return null;
    }

    const turn = value as unknown as ActiveDurableChatTurn;
    const draft = parseDurableDraft(value.draft);
    return draft === undefined ? turn : { ...turn, draft };
  } catch {
    try {
      storage.removeItem(storageKey);
    } catch {
      // Storage may have become unavailable between reads.
    }
    return null;
  }
}

function writeActiveDurableChatTurn(turn: ActiveDurableChatTurn): void {
  try {
    getSessionStorage()?.setItem(
      getActiveDurableChatTurnKey(turn.workspaceId, turn.userId, turn.conversationId),
      JSON.stringify(turn),
    );
  } catch {
    // Private browsing and quota failures must not make chat unavailable.
  }
}

function clearActiveDurableChatTurn(turn: ActiveDurableChatTurn): void {
  try {
    getSessionStorage()?.removeItem(
      getActiveDurableChatTurnKey(turn.workspaceId, turn.userId, turn.conversationId),
    );
  } catch {
    // Storage is only a reconnect aid.
  }
}

function createDurableTurnId(prefix: 'run' | 'turn'): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return `${prefix}-${crypto.randomUUID()}`;
  }

  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function waitForReconnect(delayMs: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(resolve, delayMs);
    signal.addEventListener(
      'abort',
      () => {
        window.clearTimeout(timer);
        reject(new DOMException('aborted', 'AbortError'));
      },
      { once: true },
    );
  });
}

function buildEntityConversationId(scope: Scope, input: UseEntityChatInput): string {
  return `crm-${scope.workspaceId}-${scope.userId}-${input.contextEntityType}-${input.contextEntityId}`;
}

function getContextRefKey(ref: ChatContextRef): string {
  return `${ref.type}:${ref.id}`;
}

function getDefaultContextRef(input: UseEntityChatInput): ChatContextRef | null {
  if (input.contextEntityType === 'deal') {
    return {
      type: 'deal',
      id: input.contextEntityId,
      label: i18n.t('crm.features.dynamic.features.ai.chat.hooks.useentitychat.22', {
        p0: input.contextEntityId,
      }),
    };
  }

  if (input.contextEntityType === 'contact') {
    return {
      type: 'contact',
      id: input.contextEntityId,
      label: i18n.t('crm.features.dynamic.features.ai.chat.hooks.useentitychat.23', {
        p0: input.contextEntityId,
      }),
    };
  }

  return null;
}

function getContextRefsToMerge(
  input: UseEntityChatInput,
  refs: ChatContextRef[] | undefined,
): ChatContextRef[] {
  const defaultRef = getDefaultContextRef(input);
  return defaultRef === null ? (refs ?? []) : [defaultRef, ...(refs ?? [])];
}

function mergeContextRefs(
  input: UseEntityChatInput,
  refs: ChatContextRef[] | undefined,
): ChatContextRef[] {
  const merged = new Map<string, ChatContextRef>();

  for (const ref of getContextRefsToMerge(input, refs)) {
    merged.set(getContextRefKey(ref), ref);
  }

  return Array.from(merged.values());
}

function readCommandPresentation(payload: unknown): { uiCommand?: UiCommandRequest } {
  if (!isRecord(payload) || payload.type !== 'ui_command_v1') return {};
  const parsed = uiCommandSchema.safeParse({
    commandId: payload.commandId,
    params: payload.params,
  });
  return parsed.success ? { uiCommand: parsed.data } : {};
}
function toUiMessage(message: {
  id: string;
  role: Message['role'];
  content: string;
  uiCommand?: UiCommandRequest;
  structuredPayload?: StructuredMessagePayload | null;
  toolCalls?: Message['toolCalls'] | null;
  toolCallId?: string | null;
  agentId?: string | null;
  createdAt: string;
  routeDiagnostic?: Message['routeDiagnostic'];
  traceId?: string;
}): ChatUiMessage {
  const command = readCommandPresentation(message.structuredPayload);
  const storedPayload: unknown = message.structuredPayload;
  const structuredPayload =
    command.uiCommand && isRecord(storedPayload)
      ? isRecord(storedPayload.attachment) &&
        storedPayload.attachment.type === 'chat_file_attachment_v1'
        ? (storedPayload.attachment as unknown as StructuredMessagePayload)
        : undefined
      : message.structuredPayload;
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    ...command,
    ...(message.uiCommand === undefined ? {} : { uiCommand: message.uiCommand }),
    ...(structuredPayload ? { structuredPayload } : {}),
    ...(message.toolCalls ? { toolCalls: message.toolCalls } : {}),
    ...(message.toolCallId ? { toolCallId: message.toolCallId } : {}),
    ...(message.agentId ? { agentId: message.agentId } : {}),
    ...(message.routeDiagnostic ? { routeDiagnostic: message.routeDiagnostic } : {}),
    ...(message.traceId ? { traceId: message.traceId } : {}),
    createdAt: new Date(message.createdAt),
  };
}

function recoverCompletedConversation(
  response: ChatConversationResponse,
  requestMessage: string,
): ChatUiMessage[] | null {
  let userIndex = -1;
  for (let index = response.messages.length - 1; index >= 0; index -= 1) {
    const message = response.messages[index];
    if (message?.role === 'user' && message.content.trim() === requestMessage.trim()) {
      userIndex = index;
      break;
    }
  }
  if (userIndex < 0) return null;
  const hasCompletedAssistant = response.messages
    .slice(userIndex + 1)
    .some(
      (message) =>
        message.role === 'assistant' &&
        (message.content.trim() !== '' || message.structuredPayload != null),
    );
  return hasCompletedAssistant ? response.messages.map(toUiMessage) : null;
}

/**
 * Appends the indeterminate-outcome explanation to a failure without losing
 * the original diagnostic text: the user still sees what broke, plus the
 * warning that the CRM effect is unconfirmed and how a retry behaves.
 */
function appendErrorNote(error: unknown, note: string): unknown {
  if (error instanceof ApiError) {
    return new ApiError(error.status, `${error.message} ${note}`, error.code, error.details);
  }
  return error;
}

function commandErrorKey(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const details = error.details;
  const code =
    typeof details === 'object' &&
    details !== null &&
    'code' in details &&
    typeof details.code === 'string'
      ? details.code
      : error.code;
  return commandClarificationKey(code);
}

function buildErrorMessage(error: unknown): Message {
  if (error instanceof ApiError && error.code === 'quota_exceeded') {
    return {
      id: `chat-error-${Date.now()}`,
      role: 'assistant',
      content: i18n.t('crm.ai_chat.quota.exceeded'),
      error: { message: i18n.t('crm.ai_chat.quota.exceeded'), code: error.code },
      createdAt: new Date(),
    };
  }
  const clarification = commandErrorKey(error);
  if (clarification)
    return {
      id: `chat-error-${Date.now()}`,
      role: 'assistant',
      content: i18n.t(clarification),
      createdAt: new Date(),
    };
  const errorMessage =
    error instanceof ApiError
      ? normalizeChatErrorMessage(error.message, 'Chat request failed')
      : null;
  const traceId =
    error instanceof ApiError &&
    typeof error.details === 'object' &&
    error.details !== null &&
    'traceId' in error.details &&
    typeof error.details.traceId === 'string'
      ? error.details.traceId
      : null;
  const meta =
    error instanceof ApiError
      ? [
          `HTTP ${error.status}`,
          ...(error.code === undefined ? [] : [`code: ${error.code}`]),
          ...(traceId === null ? [] : [`traceId: ${traceId}`]),
        ].join(' · ')
      : null;
  // A deadline expiry is not a provider failure: the provider did answer, the
  // turn merely ran out of time. Wrapping it in the generic "could not get an
  // answer from the LLM provider" notice would be misleading, so the localized
  // deadline text is shown verbatim.
  const plainNotice = error instanceof ApiError && error.code === 'turn_deadline_exceeded';
  const content = plainNotice
    ? `${errorMessage ?? ''}${meta === null ? '' : `\n\n${meta}`}`
    : error instanceof ApiError
      ? i18n.t('crm.features.dynamic.features.ai.chat.hooks.useentitychat.24', {
          p0: errorMessage ?? 'Chat request failed',
          p1: meta === null ? '' : `\n\n${meta}`,
        })
      : i18n.t(
          'crm.features.features.ai.chat.hooks.useentitychat.ne_udalos_poluchit_otvet_ot_llm_provayder',
        );
  const userFacingMessage = plainNotice
    ? (errorMessage ?? '')
    : error instanceof ApiError
      ? i18n.t('crm.features.dynamic.features.ai.chat.hooks.useentitychat.24', {
          p0: errorMessage ?? 'Chat request failed',
          p1: '',
        })
      : content;

  return {
    id: `chat-error-${Date.now()}`,
    role: 'assistant',
    content,
    error: {
      message: userFacingMessage,
      ...(error instanceof ApiError && error.code !== undefined ? { code: error.code } : {}),
      ...(traceId === null ? {} : { traceId }),
    },
    createdAt: new Date(),
  };
}

function requestDisplayText(request: PostChatMessageInput): string {
  return request.commandId !== undefined
    ? commandLabel(request, request.locale ?? 'ru')
    : request.message;
}
/** Hydrated pages own their projections; persisted IDs define the merge identity. */
function mergeHistoryPage(current: ChatUiMessage[], incoming: ChatUiMessage[]): ChatUiMessage[] {
  const byId = new Map(current.map((item) => [item.id, item]));
  for (const item of incoming) {
    const previous = byId.get(item.id);
    byId.set(item.id, {
      ...previous,
      ...item,
      ...(previous?.reasoning === undefined || item.reasoning !== undefined
        ? {}
        : { reasoning: previous.reasoning }),
      ...(previous?.agentId === undefined || item.agentId !== undefined
        ? {}
        : { agentId: previous.agentId }),
    });
  }
  return [...byId.values()].sort(
    (left, right) =>
      left.createdAt.getTime() - right.createdAt.getTime() ||
      (left.id < right.id ? -1 : left.id > right.id ? 1 : 0),
  );
}

function mergeDoneHistory(
  current: ChatUiMessage[],
  incoming: ChatUiMessage[],
  transientIds: Set<string>,
  transientToolPrefix: string,
  loadedIds: Set<string>,
): ChatUiMessage[] {
  const incomingIds = new Set(incoming.map((item) => item.id));
  const retained = current.filter(
    (item) =>
      !transientIds.has(item.id) &&
      !item.id.startsWith(transientToolPrefix) &&
      (loadedIds.has(item.id) || incomingIds.has(item.id)),
  );
  let latestUser = -1;
  for (let index = incoming.length - 1; index >= 0; index -= 1) {
    if (incoming[index]?.role === 'user') {
      latestUser = index;
      break;
    }
  }
  const loaded = new Set(retained.map((item) => item.id));
  // Old snapshots must not resurrect unloaded pages or downgrade hydrated cards.
  const currentTurn = incoming.filter((item, index) => index >= latestUser || loaded.has(item.id));
  const previous = new Map(retained.map((item) => [item.id, item]));
  return mergeHistoryPage(
    retained,
    currentTurn.map((item) => {
      const old = previous.get(item.id);
      const index = incoming.findIndex((candidate) => candidate.id === item.id);
      if (old !== undefined && index < latestUser) {
        const previousApproval = parseApprovalItem(old);
        const currentApproval = parseApprovalItem(item);
        // Keep older hydrated history, except a canonical terminal transition
        // for the same pending approval. Never resurrect a terminal card.
        if (
          previousApproval?.status === 'pending' &&
          currentApproval !== null &&
          currentApproval.id === previousApproval.id &&
          ['cancelled', 'rejected', 'expired', 'approved'].includes(currentApproval.status)
        )
          return item;
        return old;
      }
      return item;
    }),
  );
}

function buildUserMessage(content: string, request?: PostChatMessageInput): ChatUiMessage {
  return {
    id: `chat-user-${Date.now()}`,
    role: 'user',
    content,
    ...(request?.commandId === undefined
      ? {}
      : { uiCommand: { commandId: request.commandId, params: request.params } }),
    createdAt: new Date(),
  };
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function buildStreamingAssistantMessage(
  gatewayProfile: UseEntityChatInput['gatewayProfile'],
  openRouterModel: UseEntityChatInput['openRouterModel'],
): Message {
  const requestedGateway =
    gatewayProfile ?? (openRouterModel === undefined ? 'neuraldeep' : 'openrouter');
  return {
    id: `chat-assistant-${Date.now()}`,
    role: 'assistant',
    content: '',
    createdAt: new Date(),
    requestedRoute:
      requestedGateway === 'neuraldeep'
        ? { provider: 'neuraldeep', model: 'default' }
        : requestedGateway === 'govard'
          ? { provider: 'govard', model: 'opencode-go/deepseek-v4.1-flash' }
          : requestedGateway === 'opencode'
            ? { provider: 'opencode', model: 'space-bunny-free' }
            : { provider: 'openrouter', model: openRouterModel ?? 'default' },
  };
}

function safeJsonContent(value: unknown): string {
  try {
    return JSON.stringify(value);
  } catch {
    return JSON.stringify({ error: 'Unable to serialize tool result' });
  }
}

function getAssistantDeltaContent(event: ChatStreamEvent): string | null {
  return event.type === 'assistant_delta' ? event.payload.content : null;
}

function getReasoningDeltaContent(event: ChatStreamEvent): string | null {
  return event.type === 'reasoning_delta' ? event.payload.content : null;
}

function getStreamError(event: ChatStreamEvent): { message: string; code?: string } | null {
  if (event.type !== 'error') return null;
  return {
    message: normalizeChatErrorMessage(event.payload.error?.message, 'Chat request failed'),
    ...(event.payload.error?.code === undefined ? {} : { code: event.payload.error.code }),
  };
}

function normalizeStreamMessage(message: AiChatMessageSnapshot): {
  id: string;
  role: Message['role'];
  content: string;
  uiCommand?: UiCommandRequest;
  structuredPayload?: StructuredMessagePayload | null;
  toolCalls?: Message['toolCalls'] | null;
  toolCallId?: string | null;
  createdAt: string;
} {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    ...((message as AiChatMessageSnapshot & { uiCommand?: UiCommandRequest }).uiCommand ===
    undefined
      ? {}
      : {
          uiCommand: (message as AiChatMessageSnapshot & { uiCommand: UiCommandRequest }).uiCommand,
        }),
    ...(message.structuredPayload === undefined
      ? {}
      : { structuredPayload: message.structuredPayload }),
    ...(message.toolCalls === undefined ? {} : { toolCalls: message.toolCalls }),
    ...(message.toolCallId === undefined ? {} : { toolCallId: message.toolCallId }),
    createdAt:
      message.createdAt instanceof Date
        ? message.createdAt.toISOString()
        : (message.createdAt ?? new Date().toISOString()),
  };
}

function getDonePayload(event: ChatStreamEvent): {
  messages: Array<{
    id: string;
    role: Message['role'];
    content: string;
    uiCommand?: UiCommandRequest;
    structuredPayload?: StructuredMessagePayload | null;
    toolCalls?: Message['toolCalls'] | null;
    toolCallId?: string | null;
    createdAt: string;
  }>;
  usage: UseAgentReturn['usage'];
  routeDiagnostic?: Message['routeDiagnostic'];
  traceId?: string;
} | null {
  return event.type === 'done'
    ? {
        messages: (event.payload.messages ?? []).map(normalizeStreamMessage),
        usage: event.payload.usage ?? null,
        ...(event.payload.routeDiagnostic === undefined
          ? {}
          : { routeDiagnostic: event.payload.routeDiagnostic }),
        ...(event.payload.traceId === undefined ? {} : { traceId: event.payload.traceId }),
      }
    : null;
}

function parseApprovalItem(message: Message): ApprovalItem | null {
  if (message.role !== 'tool') {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(message.content);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return null;
  }

  const record = parsed as Record<string, unknown>;
  if (
    typeof record.approvalId !== 'string' ||
    typeof record.kind !== 'string' ||
    typeof record.status !== 'string' ||
    typeof record.toolName !== 'string' ||
    typeof record.expiresAt !== 'string' ||
    typeof record.args !== 'object' ||
    record.args === null
  ) {
    return null;
  }

  return {
    id: record.approvalId,
    kind: record.kind as ApprovalItem['kind'],
    status: record.status as ApprovalItem['status'],
    payload: {
      toolName: record.toolName,
      args: record.args as Record<string, unknown>,
      ...(typeof record.preview === 'object' && record.preview !== null
        ? { preview: record.preview }
        : {}),
    },
    expiresAt: record.expiresAt,
    ...(record.presentation !== undefined && isApprovalPresentationV1(record.presentation)
      ? { presentation: record.presentation }
      : {}),
  };
}

function extractApprovalItems(messages: Message[]): ApprovalItem[] {
  return messages.flatMap((message) => {
    const approval = parseApprovalItem(message);
    return approval === null ? [] : [approval];
  });
}

function toUiApproval(
  approval: ApprovalItem,
  conversationId: string,
): EntityChatAgent['pendingApprovals'][number] {
  const toolName =
    typeof approval.payload.toolName === 'string' ? approval.payload.toolName : approval.kind;
  const args =
    typeof approval.payload.args === 'object' && approval.payload.args !== null
      ? (approval.payload.args as Record<string, unknown>)
      : approval.payload;

  return {
    id: approval.id,
    conversationId,
    toolName,
    args,
    createdAt: new Date(),
    timeoutAt: new Date(approval.expiresAt).getTime(),
    status: approval.status,
    ...(approval.presentation == null ? {} : { presentation: approval.presentation }),
  };
}

export function useEntityChat(scope: Scope | null, input: UseEntityChatInput): EntityChatAgent {
  const conversationId = useMemo(
    () =>
      input.conversationId ??
      (scope
        ? buildEntityConversationId(scope, input)
        : `crm-anonymous-anonymous-${input.contextEntityType}-${input.contextEntityId}`),
    [input, scope],
  );
  const [messages, setMessages] = useState<ChatUiMessage[]>([]);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;
  const [historyPage, setHistoryPage] = useState<ChatConversationResponse['historyPage']>();
  const [isLoadingEarlier, setIsLoadingEarlier] = useState(false);
  const [earlierHistoryError, setEarlierHistoryError] = useState(false);
  const historyControllerRef = useRef<AbortController | null>(null);
  const historyGenerationRef = useRef(0);
  const loadedPagesRef = useRef<Array<{ options: ChatHistoryOptions; ids: Set<string> }>>([]);
  const [pendingApprovals, setPendingApprovals] = useState<ApprovalItem[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [usage, setUsage] = useState<UseAgentReturn['usage']>(null);
  const [selectedEntity, setSelectedEntity] = useState<NonNullable<
    ChatConversationResponse['selectedEntity']
  > | null>(null);
  const [selectedDeal, setSelectedDeal] = useState<{ id: string; label: string } | null>(null);
  const mutationVersionRef = useRef(0);
  const activeStreamControllerRef = useRef<AbortController | null>(null);
  const activeDurableTurnRef = useRef<ActiveDurableChatTurn | null>(null);
  const retryableRequestRef = useRef<PostChatMessageInput | null>(null);
  /**
   * A turn whose outcome could not be confirmed while durable progress had
   * already been observed. Its retry must resume the SAME durable identity so
   * the server replays the ledger instead of re-running the agent; a fresh
   * identity would mint a new gateway idempotency key and could repeat the
   * mutation. Null means the last failure had no durable progress, so an
   * ordinary fresh retry is safe.
   */
  const pendingIndeterminateTurnRef = useRef<PostChatMessageInput | null>(null);
  const reasoningAccumulatorRef = useRef('');
  const activeStreamIdRef = useRef(0);
  const resumeActiveTurnRef = useRef<((turn: ActiveDurableChatTurn) => void) | null>(null);
  const resumedTurnKeyRef = useRef<string | null>(null);
  const isMountedRef = useRef(true);
  const scopeIdentity = scope === null ? null : `${scope.workspaceId}:${scope.userId}`;
  // Read through a ref so a queued client action always carries the scope that
  // is live at the moment the envelope arrives, without rebinding the streaming
  // callback on every scope object change. The bus re-verifies it before
  // anything navigates, so a stale value fails closed rather than executing.
  const chatActionScope = useMemo(
    () => (scope === null ? null : { workspaceId: scope.workspaceId, userId: scope.userId }),
    [scope],
  );
  const chatActionScopeRef = useRef<ChatClientActionScopeIdentity | null>(chatActionScope);
  chatActionScopeRef.current = chatActionScope;
  const shouldHydrateConversation =
    input.conversationId !== undefined || input.contextEntityType !== 'none';

  useEffect(() => {
    isMountedRef.current = true;
    // The server keeps producing a durable turn without a reader, so the last
    // rendered draft must survive every way this hook loses its stream: route
    // change (effect cleanup), reload/close (pagehide) and backgrounding.
    const persistActiveTurn = (): void => {
      const activeTurn = activeDurableTurnRef.current;
      if (activeTurn !== null) {
        writeActiveDurableChatTurn(activeTurn);
      }
    };
    const persistWhenHidden = (): void => {
      if (document.visibilityState === 'hidden') {
        persistActiveTurn();
      }
    };
    window.addEventListener('pagehide', persistActiveTurn);
    document.addEventListener('visibilitychange', persistWhenHidden);
    return () => {
      persistActiveTurn();
      window.removeEventListener('pagehide', persistActiveTurn);
      document.removeEventListener('visibilitychange', persistWhenHidden);
      isMountedRef.current = false;
      activeStreamControllerRef.current?.abort();
      historyControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    // Switching conversations detaches this hook from its stream but must not
    // erase the durable turn: the server keeps producing it and returning to
    // that conversation has to resume the answer.
    const activeTurn = activeDurableTurnRef.current;
    if (activeTurn !== null) {
      writeActiveDurableChatTurn(activeTurn);
    }
    activeStreamControllerRef.current?.abort();
    activeStreamControllerRef.current = null;
    activeDurableTurnRef.current = null;
    retryableRequestRef.current = null;
    pendingIndeterminateTurnRef.current = null;
    activeStreamIdRef.current += 1;
    resumedTurnKeyRef.current = null;
    setIsStreaming(false);
    mutationVersionRef.current += 1;
    historyControllerRef.current?.abort();
    historyControllerRef.current = null;
    historyGenerationRef.current += 1;
    loadedPagesRef.current = [];
    setHistoryPage(undefined);
    setIsLoadingEarlier(false);
    setEarlierHistoryError(false);
    setMessages([]);
    setPendingApprovals([]);
    setUsage(null);
    setSelectedEntity(null);
    setSelectedDeal(null);
  }, [conversationId, scopeIdentity]);

  useEffect(() => {
    if (scope === null) {
      return;
    }

    let cancelled = false;
    const hydrationVersion = mutationVersionRef.current;
    const resumeStoredTurn = () => {
      if (cancelled || mutationVersionRef.current !== hydrationVersion) return;
      const turn = readActiveDurableChatTurn(scope, conversationId);
      if (turn === null) return;
      const turnKey = `${turn.runId}:${turn.turnId}`;
      if (resumedTurnKeyRef.current === turnKey) return;
      resumedTurnKeyRef.current = turnKey;
      resumeActiveTurnRef.current?.(turn);
    };

    if (!shouldHydrateConversation) {
      resumeStoredTurn();
      return () => {
        cancelled = true;
      };
    }

    void getChatConversation(conversationId, { turnLimit: 20 })
      .then((response) => {
        if (cancelled || mutationVersionRef.current !== hydrationVersion) {
          return;
        }

        const hydratedMessages = response.messages.map(toUiMessage);
        setHistoryPage(response.historyPage);
        loadedPagesRef.current = [
          { options: { turnLimit: 20 }, ids: new Set(hydratedMessages.map((item) => item.id)) },
        ];
        setMessages(hydratedMessages);
        setPendingApprovals(extractApprovalItems(hydratedMessages));
        setSelectedEntity(response.selectedEntity ?? null);
        setSelectedDeal(response.selectedDeal ?? null);
        resumeStoredTurn();
      })
      .catch(() => {
        if (cancelled || mutationVersionRef.current !== hydrationVersion) {
          return;
        }
        setMessages([]);
        setPendingApprovals([]);
        setSelectedEntity(null);
        setSelectedDeal(null);
        resumeStoredTurn();
      });

    return () => {
      cancelled = true;
    };
  }, [conversationId, scope, scopeIdentity, shouldHydrateConversation]);

  const runDurableTurn = useCallback(
    async (
      turn: ActiveDurableChatTurn,
      appendOptimisticUserMessage: boolean,
      options?: { indeterminateResume?: boolean; resumedFromStorage?: boolean },
    ): Promise<void> => {
      const indeterminateResume = options?.indeterminateResume === true;
      const resumedFromStorage = options?.resumedFromStorage === true;
      mutationVersionRef.current += 1;
      setIsStreaming(true);
      const streamingAssistantMessage = buildStreamingAssistantMessage(
        input.gatewayProfile,
        turn.request.openRouterModel ?? input.openRouterModel,
      );
      const streamController = new AbortController();
      reasoningAccumulatorRef.current = '';
      activeStreamControllerRef.current?.abort();
      activeStreamControllerRef.current = streamController;
      activeDurableTurnRef.current = turn;
      activeStreamIdRef.current += 1;
      const streamId = activeStreamIdRef.current;
      let assistantStarted = false;
      let terminal = false;
      const replayGuard = createAiChatEventReplayGuard();

      const consumeStream = async (): Promise<void> => {
        for await (const event of postChatMessageStream(conversationId, turn.request, {
          signal: streamController.signal,
          ...(turn.cursor === null ? {} : { cursor: turn.cursor }),
        })) {
          if (streamId !== activeStreamIdRef.current || streamController.signal.aborted) return;

          if (
            event.conversationId !== turn.conversationId ||
            event.runId !== turn.runId ||
            event.turnId !== turn.turnId
          ) {
            throw new ApiError(
              500,
              'Chat stream event identity does not match the active turn',
              'chat_stream_identity_mismatch',
            );
          }
          const validation = replayGuard.validate(event);
          if (!validation.ok) {
            if (
              validation.code === 'duplicate_event' ||
              validation.code === 'out_of_order_sequence' ||
              validation.code === 'event_after_terminal'
            ) {
              continue;
            }
            throw new ApiError(500, validation.message, validation.code);
          }

          turn.cursor = {
            eventId: event.eventId,
            runId: event.runId,
            turnId: event.turnId,
            sequence: event.sequence,
          };
          writeActiveDurableChatTurn(turn);

          if (event.type === 'tool_call') {
            assistantStarted = true;
            const draftToolCalls = turn.draft?.toolCalls ?? [];
            turn.draft = {
              content: turn.draft?.content ?? '',
              ...(turn.draft?.reasoning === undefined ? {} : { reasoning: turn.draft.reasoning }),
              toolCalls: draftToolCalls.some((call) => call.id === event.payload.call.id)
                ? draftToolCalls.map((call) =>
                    call.id === event.payload.call.id ? event.payload.call : call,
                  )
                : [...draftToolCalls, event.payload.call],
            };
            setMessages((current) => {
              const existing = current.find(
                (message) => message.id === streamingAssistantMessage.id,
              );
              const toolCalls = existing?.toolCalls ?? [];
              const nextToolCalls = toolCalls.some((call) => call.id === event.payload.call.id)
                ? toolCalls.map((call) =>
                    call.id === event.payload.call.id ? event.payload.call : call,
                  )
                : [...toolCalls, event.payload.call];
              return existing === undefined
                ? [...current, { ...streamingAssistantMessage, toolCalls: nextToolCalls }]
                : current.map((message) =>
                    message.id === existing.id ? { ...message, toolCalls: nextToolCalls } : message,
                  );
            });
            continue;
          }

          if (event.type === 'tool_result') {
            const toolMessageId = `${turn.runId}:${turn.turnId}:tool:${event.payload.result.toolCallId}`;
            const toolMessage: ChatUiMessage = {
              id: toolMessageId,
              role: 'tool',
              content: safeJsonContent(event.payload.result),
              toolCallId: event.payload.result.toolCallId,
              ...(event.agentId === undefined ? {} : { agentId: event.agentId }),
              createdAt: new Date(),
            };
            setMessages((current) =>
              current.some((message) => message.id === toolMessageId)
                ? current.map((message) => (message.id === toolMessageId ? toolMessage : message))
                : [...current, toolMessage],
            );
            continue;
          }

          if (event.type === 'approval_required') {
            const approval = event.payload.approval;
            const presentation =
              'presentation' in approval && isApprovalPresentationV1(approval.presentation)
                ? approval.presentation
                : undefined;
            const normalizedApproval: ApprovalItem = {
              id: approval.id,
              kind: 'tool_call',
              status: approval.status,
              payload: { toolName: approval.toolName, args: approval.args },
              ...(presentation === undefined ? {} : { presentation }),
              expiresAt: new Date(approval.timeoutAt).toISOString(),
            };
            const toolMessageId = `${turn.runId}:${turn.turnId}:tool:${approval.id}`;
            const toolMessage: Message = {
              id: toolMessageId,
              role: 'tool',
              content: safeJsonContent({
                approvalId: approval.id,
                kind: 'tool_call',
                status: approval.status,
                toolName: approval.toolName,
                args: approval.args,
                expiresAt: normalizedApproval.expiresAt,
                ...(presentation === undefined ? {} : { presentation }),
              }),
              createdAt: new Date(),
            };
            setMessages((current) =>
              current.some((message) => message.id === toolMessageId)
                ? current.map((message) => (message.id === toolMessageId ? toolMessage : message))
                : [...current, toolMessage],
            );
            setPendingApprovals((current) =>
              current.some((item) => item.id === approval.id)
                ? current.map((item) => (item.id === approval.id ? normalizedApproval : item))
                : [...current, normalizedApproval],
            );
            continue;
          }

          if (event.type === 'structured_payload') {
            setMessages((current) =>
              current.some((message) => message.id === event.payload.messageId)
                ? current.map((message) =>
                    message.id === event.payload.messageId
                      ? { ...message, structuredPayload: event.payload.payload }
                      : message,
                  )
                : [
                    ...current,
                    {
                      id: event.payload.messageId,
                      role: 'assistant',
                      content: '',
                      structuredPayload: event.payload.payload,
                      createdAt: new Date(),
                    },
                  ],
            );
            continue;
          }

          // PR #973 MVP: the turn asked the browser to move. Dispatching is
          // navigation-free here — the bus dedupes by eventId, refuses a command
          // produced under a different session, and the page that owns the router
          // is the only place that acts on it. A replayed or reconnected envelope
          // is dropped by the bus before it can execute.
          //
          // The SSE boundary validates envelope shape only (same as every other
          // event type), so the action payload is still `unknown` data here. It is
          // sanitized at this last point before the bus: a malformed action is
          // dropped and the turn carries on, rather than reaching the router.
          if (event.type === 'client_action') {
            const actionScope = chatActionScopeRef.current;
            const action = sanitizeAiChatClientAction(event.payload.action);
            if (actionScope !== null && action !== undefined) {
              enqueueChatClientAction({
                eventId: event.eventId,
                conversationId: event.conversationId,
                scope: actionScope,
                action,
              });
            }
            continue;
          }

          if (event.type === 'turn_status') {
            if (event.payload.status === 'failed') {
              terminal = true;
              clearActiveDurableChatTurn(turn);
              const failure = new ApiError(
                502,
                normalizeChatErrorMessage(event.payload.error?.message, 'Chat turn failed'),
                event.payload.error?.code,
              );
              // A server-declared failure is NOT positive proof that no CRM
              // effect exists: the backend may have persisted an approval or
              // committed a mutation before the terminal event was written,
              // and delivery of the durable events to this browser proves
              // nothing. Every terminal failure is therefore routed through
              // the shared recovery + indeterminate path, which restores the
              // server state and keeps the ORIGINAL turn identity for retry
              // (a fresh identity would mint a new gateway idempotency key).
              throw failure;
            }
            if (event.payload.status === 'cancelled') {
              terminal = true;
              clearActiveDurableChatTurn(turn);
              if (indeterminateResume) {
                // The replay of a cancelled turn can only return the saved
                // terminal result — a cancelled turn never resumes execution.
                // Show that terminal state as-is, state the unconfirmed
                // outcome, and withdraw the retry so no endlessly repeating
                // button remains.
                pendingIndeterminateTurnRef.current = null;
                retryableRequestRef.current = null;
                setMessages((current) => [
                  ...current.filter(keepStreamingPlaceholder),
                  buildErrorMessage(
                    appendErrorNote(
                      new ApiError(
                        499,
                        i18n.t('crm.ai_chat.error.cancelled_outcome'),
                        'cancelled_chat_turn',
                      ),
                      i18n.t('crm.ai_chat.error.indeterminate_result'),
                    ),
                  ),
                ]);
                return;
              }
              // A disconnect/cleanup abort (conversation switch, unmount, lost
              // focus) is not a user-initiated cancel. The turn may already have
              // produced durable CRM effects, so treat it as indeterminate and
              // keep the original identity for a safe retry.
              if (resumedFromStorage) {
                pendingIndeterminateTurnRef.current = turn.request;
                retryableRequestRef.current = turn.request;
                const deadlineExceeded = event.payload.error?.code === 'turn_deadline_exceeded';
                setMessages((current) => [
                  ...current.filter(keepStreamingPlaceholder),
                  buildErrorMessage(
                    appendErrorNote(
                      new ApiError(
                        502,
                        i18n.t('crm.ai_chat.error.request_failed'),
                        'chat_turn_aborted',
                      ),
                      i18n.t(
                        deadlineExceeded
                          ? 'crm.ai_chat.error.unconfirmed_outcome'
                          : 'crm.ai_chat.error.indeterminate_result',
                      ),
                    ),
                  ),
                ]);
                return;
              }
              // A user-initiated cancel does not prove the absence of a CRM
              // effect: a tool may have completed before the abort. Keep the
              // ORIGINAL identity so a retry replays the durable ledger
              // instead of re-running the agent under a fresh idempotency key.
              // The same holds for a deadline expiry, which the server marks
              // with an error code on this otherwise identical cancelled status.
              const deadlineExceeded = event.payload.error?.code === 'turn_deadline_exceeded';
              pendingIndeterminateTurnRef.current = turn.request;
              retryableRequestRef.current = turn.request;
              setMessages((current) => [
                ...current.filter(keepStreamingPlaceholder),
                buildErrorMessage(
                  new ApiError(
                    408,
                    i18n.t(
                      deadlineExceeded
                        ? 'crm.ai_chat.error.deadline_exceeded'
                        : 'crm.ai_chat.error.cancelled_recoverable',
                    ),
                    deadlineExceeded ? 'turn_deadline_exceeded' : 'chat_turn_cancelled',
                  ),
                ),
              ]);
              return;
            }
            continue;
          }

          const streamError = getStreamError(event);
          if (streamError !== null) {
            terminal = true;
            clearActiveDurableChatTurn(turn);
            throw new ApiError(502, streamError.message, streamError.code);
          }

          const assistantDelta = getAssistantDeltaContent(event);
          if (assistantDelta !== null) {
            turn.draft = {
              content: (turn.draft?.content ?? '') + assistantDelta,
              ...(turn.draft?.reasoning === undefined ? {} : { reasoning: turn.draft.reasoning }),
              ...(turn.draft?.toolCalls === undefined ? {} : { toolCalls: turn.draft.toolCalls }),
            };
            if (!assistantStarted) {
              assistantStarted = true;
              setMessages((current) => [
                ...current,
                { ...streamingAssistantMessage, content: assistantDelta },
              ]);
              continue;
            }
            setMessages((current) =>
              current.map((message) =>
                message.id === streamingAssistantMessage.id
                  ? { ...message, content: message.content + assistantDelta }
                  : message,
              ),
            );
            continue;
          }

          const reasoningDelta = getReasoningDeltaContent(event);
          if (reasoningDelta !== null) {
            reasoningAccumulatorRef.current += reasoningDelta;
            turn.draft = {
              content: turn.draft?.content ?? '',
              reasoning: `${turn.draft?.reasoning ?? ''}${reasoningDelta}`,
              ...(turn.draft?.toolCalls === undefined ? {} : { toolCalls: turn.draft.toolCalls }),
            };
            if (!assistantStarted) {
              assistantStarted = true;
              setMessages((current) => [
                ...current,
                { ...streamingAssistantMessage, reasoning: reasoningDelta },
              ]);
              continue;
            }
            setMessages((current) =>
              current.map((message) =>
                message.id === streamingAssistantMessage.id
                  ? { ...message, reasoning: `${message.reasoning ?? ''}${reasoningDelta}` }
                  : message,
              ),
            );
            continue;
          }

          const donePayload = getDonePayload(event);
          if (donePayload === null) continue;

          terminal = true;
          clearActiveDurableChatTurn(turn);
          pendingIndeterminateTurnRef.current = null;
          retryableRequestRef.current = null;
          let lastAssistantIndex = -1;
          for (let index = donePayload.messages.length - 1; index >= 0; index -= 1) {
            if (donePayload.messages[index]?.role === 'assistant') {
              lastAssistantIndex = index;
              break;
            }
          }
          const nextMessages = donePayload.messages.map((message, index) =>
            toUiMessage({
              ...message,
              ...(index === lastAssistantIndex && donePayload.routeDiagnostic !== undefined
                ? {
                    routeDiagnostic: donePayload.routeDiagnostic,
                    ...(donePayload.traceId === undefined ? {} : { traceId: donePayload.traceId }),
                  }
                : {}),
            }),
          );
          const accumulatedReasoning = reasoningAccumulatorRef.current;
          const finalMessages =
            accumulatedReasoning.length === 0
              ? nextMessages
              : nextMessages.map((message, index) =>
                  (index === lastAssistantIndex ||
                    (message.role === 'assistant' &&
                      message.content === streamingAssistantMessage.content)) &&
                  message.reasoning === undefined
                    ? { ...message, reasoning: accumulatedReasoning }
                    : message,
                );
          // The `done` snapshot replaces the streamed messages with backend
          // copies that carry no agentId. Re-attach the executor attribution
          // from the streamed tool messages (matched by toolCallId) so the UI
          // can keep showing who answered a delegated tool call.
          setMessages((current) => {
            const agentIdByToolCallId = new Map<string, string>();
            for (const message of current) {
              if (
                message.role === 'tool' &&
                message.toolCallId !== undefined &&
                message.agentId !== undefined
              ) {
                agentIdByToolCallId.set(message.toolCallId, message.agentId);
              }
            }
            const attributed = finalMessages.map((message) => {
              if (message.role !== 'tool' || message.toolCallId === undefined) {
                return message;
              }
              const agentId = agentIdByToolCallId.get(message.toolCallId);
              return agentId === undefined ? message : { ...message, agentId };
            });
            const optimisticUser = [...current]
              .reverse()
              .find(
                (item) =>
                  item.role === 'user' &&
                  item.id.startsWith('chat-user-') &&
                  item.content === requestDisplayText(turn.request),
              );
            const merged = mergeDoneHistory(
              current,
              attributed,
              new Set([
                streamingAssistantMessage.id,
                ...(optimisticUser === undefined ? [] : [optimisticUser.id]),
              ]),
              `${turn.runId}:${turn.turnId}:tool:`,
              new Set(loadedPagesRef.current.flatMap((page) => [...page.ids])),
            );
            setPendingApprovals(extractApprovalItems(merged));
            return merged;
          });
          setUsage(donePayload.usage);
          // The deal focus is a hidden server marker, intentionally absent
          // from chat messages. Refresh only this small piece of UI state once
          // the durable `done` event confirms it has been persisted. The
          // response must not land after a conversation switch or a newer
          // turn: streamId is bumped on both, so stale fetches are dropped.
          void getChatConversation(conversationId, { turnLimit: 20 })
            .then((response) => {
              if (!isMountedRef.current || streamId !== activeStreamIdRef.current) {
                return;
              }
              if (loadedPagesRef.current.length <= 1) setHistoryPage(response.historyPage);
              const latestPage = loadedPagesRef.current[0];
              if (latestPage) {
                for (const item of response.messages) latestPage.ids.add(item.id);
              } else {
                loadedPagesRef.current = [
                  {
                    options: { turnLimit: 20 },
                    ids: new Set(response.messages.map((item) => item.id)),
                  },
                ];
              }
              setSelectedEntity(response.selectedEntity ?? null);
              setSelectedDeal(response.selectedDeal ?? null);
            })
            .catch(() => {
              // A failed refresh of this hidden marker must not erase the
              // focus the user already sees; the next turn re-syncs it.
            });
        }
      };

      // The agent bubble must exist before the first network byte: the durable
      // stream can stay silent for seconds and a composer-only spinner reads as
      // a hang. Later events update this exact message id.
      const keepStreamingPlaceholder = (message: ChatUiMessage): boolean =>
        message.id !== streamingAssistantMessage.id ||
        message.content.length > 0 ||
        (message.reasoning?.length ?? 0) > 0 ||
        (message.toolCalls?.length ?? 0) > 0;

      if (appendOptimisticUserMessage) {
        setMessages((current) => [
          ...current,
          buildUserMessage(requestDisplayText(turn.request), turn.request),
        ]);
      } else {
        // A resume replays only the events after the stored cursor, so the user
        // turn must be re-seeded whenever hydration has not persisted it yet.
        setMessages((current) =>
          current.some(
            (message) =>
              message.role === 'user' && message.content === requestDisplayText(turn.request),
          )
            ? current
            : [...current, buildUserMessage(requestDisplayText(turn.request), turn.request)],
        );
      }
      assistantStarted = true;
      const seededAssistantMessage: Message =
        turn.draft === undefined
          ? streamingAssistantMessage
          : {
              ...streamingAssistantMessage,
              content: turn.draft.content,
              ...(turn.draft.reasoning === undefined ? {} : { reasoning: turn.draft.reasoning }),
              ...(turn.draft.toolCalls === undefined ? {} : { toolCalls: turn.draft.toolCalls }),
            };
      setMessages((current) =>
        current.some((message) => message.id === streamingAssistantMessage.id)
          ? current
          : [...current, seededAssistantMessage],
      );

      try {
        let lastError: unknown = new Error('chat stream ended before terminal event');
        for (let attempt = 0; attempt <= MAX_STREAM_RECONNECTS; attempt += 1) {
          try {
            await consumeStream();
            if (terminal || streamController.signal.aborted) return;
          } catch (error) {
            if (isAbortError(error) || streamController.signal.aborted) return;
            if (terminal) throw error;
            // A structured HTTP/API rejection is not a transient transport close.
            if (error instanceof ApiError) throw error;
            lastError = error;
          }

          if (attempt === MAX_STREAM_RECONNECTS) throw lastError;
          await waitForReconnect(
            STREAM_RECONNECT_DELAYS_MS[attempt] ?? 150,
            streamController.signal,
          );
          if (streamId !== activeStreamIdRef.current || streamController.signal.aborted) return;
        }
      } catch (error) {
        if (
          isAbortError(error) ||
          streamController.signal.aborted ||
          streamId !== activeStreamIdRef.current ||
          !isMountedRef.current
        ) {
          return;
        }
        if (commandErrorKey(error) !== undefined) {
          clearActiveDurableChatTurn(turn);
          pendingIndeterminateTurnRef.current = null;
          retryableRequestRef.current = null;
          setMessages((previous) => [...previous, buildErrorMessage(error)]);
          return;
        }
        // The durable backend may have committed the final assistant message
        // or a durable mutation while the browser lost the terminal SSE frame.
        // Reconcile that exact user turn before reporting anything: recovery
        // is attempted for every failure — a server-declared failure without
        // delivered durable events is not positive proof that repeating under
        // a fresh identity is safe.
        try {
          const response = await getChatConversation(conversationId, { turnLimit: 20 });
          const recoveredMessages = recoverCompletedConversation(
            response,
            requestDisplayText(turn.request),
          );
          if (recoveredMessages !== null) {
            // CONFIRMED COMPLETION: the committed turn is authoritative.
            clearActiveDurableChatTurn(turn);
            pendingIndeterminateTurnRef.current = null;
            retryableRequestRef.current = null;
            setMessages((current) => {
              const optimisticUser = [...current]
                .reverse()
                .find(
                  (item) =>
                    item.role === 'user' &&
                    item.id.startsWith('chat-user-') &&
                    item.content === requestDisplayText(turn.request),
                );
              const merged = mergeDoneHistory(
                current,
                recoveredMessages,
                new Set([
                  streamingAssistantMessage.id,
                  ...(optimisticUser === undefined ? [] : [optimisticUser.id]),
                ]),
                `${turn.runId}:${turn.turnId}:tool:`,
                new Set(loadedPagesRef.current.flatMap((page) => [...page.ids])),
              );
              setPendingApprovals(extractApprovalItems(merged));
              return merged;
            });
            setSelectedEntity(response.selectedEntity ?? null);
            setSelectedDeal(response.selectedDeal ?? null);
            return;
          }
        } catch {
          // Recovery itself failed: the outcome stays INDETERMINATE.
        }
        if (indeterminateResume) {
          // The retry already re-issued the SAME turn identity and the server
          // replayed the ledger back into the same terminal error. Repeating
          // it again would only reproduce that error, so the retry action is
          // withdrawn and the unconfirmed outcome is stated explicitly: the
          // user is told to check the CRM before re-sending anything. The
          // original diagnostic text is preserved next to the explanation.
          pendingIndeterminateTurnRef.current = null;
          retryableRequestRef.current = null;
          setMessages((current) => [
            ...current.filter(keepStreamingPlaceholder),
            buildErrorMessage(
              appendErrorNote(error, i18n.t('crm.ai_chat.error.indeterminate_result')),
            ),
          ]);
          return;
        }
        // INDETERMINATE RESULT. The failure proves nothing about what the
        // backend already accepted: a server-declared failure may follow an
        // already persisted approval or committed mutation whose events never
        // reached this browser, and a dropped connection proves even less. A
        // blind retry would mint a fresh turn identity, and the gateway
        // idempotency key is derived from that identity, so it could execute
        // the same mutation a second time. Keep the action available but route
        // it through the existing resume contract instead: re-issuing the SAME
        // identity makes the server replay and tail the durable ledger
        // (`admitChatStreamTurn` + `tailDurableTurn`) and report the real
        // status without re-running the agent. No new idempotency mechanism is
        // involved, and the message says the outcome is unconfirmed.
        pendingIndeterminateTurnRef.current = turn.request;
        retryableRequestRef.current = turn.request;
        setMessages((current) => [
          ...current.filter(keepStreamingPlaceholder),
          buildErrorMessage(
            appendErrorNote(error, i18n.t('crm.ai_chat.error.unconfirmed_outcome')),
          ),
        ]);
      } finally {
        if (activeStreamControllerRef.current === streamController) {
          activeStreamControllerRef.current = null;
          activeDurableTurnRef.current = null;
          if (isMountedRef.current) {
            setIsStreaming(false);
          }
        }
      }
    },
    // gatewayProfile/openRouterModel are read when the streaming bubble is
    // built, so a profile switch mid-session must rebind this callback —
    // otherwise the next turn still renders under the previous profile.
    [conversationId, input.gatewayProfile, input.openRouterModel],
  );

  resumeActiveTurnRef.current = (turn) => {
    void runDurableTurn(turn, false, { resumedFromStorage: true });
  };

  const send = useCallback(
    async (
      userInput: string | UiCommandRequest,
      options?: EntityChatSendOptions,
    ): Promise<void> => {
      const trimmed =
        typeof userInput === 'string' ? userInput.trim() : commandLabel(userInput, i18n.locale);
      if (!trimmed || scope === null || activeStreamControllerRef.current !== null) return;

      const contextRefs = mergeContextRefs(input, options?.contextRefs);
      if (typeof userInput !== 'string' && commandContextRefCount(userInput, contextRefs) > 8) {
        void message.info(i18n.t('crm.ai_chat.context_chips.max_reached'));
        throw new Error('Chat command context exceeds the context ref limit');
      }

      const runId = createDurableTurnId('run');
      const turnId = createDurableTurnId('turn');
      const taskCorrection =
        typeof userInput === 'string'
          ? taskCorrectionForMessage(messagesRef.current, trimmed)
          : undefined;
      const compoundSelection =
        typeof userInput === 'string'
          ? compoundSelectionForMessage(messagesRef.current, trimmed)
          : undefined;
      const clientTimeZone = browserChatTimeZone();
      const turn: ActiveDurableChatTurn = {
        version: 1,
        workspaceId: scope.workspaceId,
        userId: scope.userId,
        conversationId,
        runId,
        turnId,
        cursor: null,
        request: {
          ...(typeof userInput === 'string' ? { message: trimmed } : userInput),
          ...(compoundSelection === undefined ? {} : { compoundSelection }),
          ...(taskCorrection === undefined ? {} : { taskCorrection }),
          ...(typeof userInput === 'string' &&
          batchCorrectionForMessage(messagesRef.current, trimmed)
            ? { batchCorrection: batchCorrectionForMessage(messagesRef.current, trimmed) }
            : {}),
          locale: i18n.locale,
          ...(clientTimeZone === undefined ? {} : { clientTimeZone }),
          contextEntityType: input.contextEntityType,
          contextEntityId: input.contextEntityId,
          contextRefs,
          runId,
          turnId,
          ...(isReasoningEnabled() ? {} : { reasoning: false }),
          ...(options?.meetingSnapshot === undefined
            ? {}
            : { meetingSnapshot: options.meetingSnapshot }),
          ...(options?.attachmentId === undefined ? {} : { attachmentId: options.attachmentId }),
          ...(options?.openRouterModel === undefined && input.openRouterModel === undefined
            ? {}
            : { openRouterModel: options?.openRouterModel ?? input.openRouterModel }),
          ...(input.gatewayProfile === undefined ? {} : { gatewayProfile: input.gatewayProfile }),
        },
      };
      resumedTurnKeyRef.current = `${runId}:${turnId}`;
      // A fresh user message supersedes any unresolved indeterminate turn: the
      // user chose a new request, so its retry no longer applies.
      pendingIndeterminateTurnRef.current = null;
      retryableRequestRef.current = turn.request;
      writeActiveDurableChatTurn(turn);
      await runDurableTurn(turn, true);
    },
    // i18n.locale is read at send time so a ru ⇄ en switch applies to the
    // next new turn. Retry paths reuse retryableRequestRef (the stored
    // request with its original locale) and never re-read this closure.
    [conversationId, input, runDurableTurn, scope, i18n.locale],
  );

  const approve = useCallback(
    async (approvalId: string, decision: 'approve' | 'reject'): Promise<void> => {
      const updatedApproval = await decideApproval({ approvalId, decision });
      setPendingApprovals((current) =>
        current.map((approval) => (approval.id === approvalId ? updatedApproval : approval)),
      );
    },
    [],
  );

  const applyApprovalDecision = useCallback((updatedApproval: ApprovalItem): void => {
    setMessages((current) =>
      current.map((message) => {
        const approval = parseApprovalItem(message);
        if (approval === null || approval.id !== updatedApproval.id) {
          return message;
        }
        let record: Record<string, unknown>;
        try {
          const parsed: unknown = JSON.parse(message.content);
          if (typeof parsed !== 'object' || parsed === null) {
            return message;
          }
          record = parsed as Record<string, unknown>;
        } catch {
          return message;
        }
        return {
          ...message,
          content: JSON.stringify({
            ...record,
            status: updatedApproval.status,
            ...(updatedApproval.decidedAt === undefined
              ? {}
              : { decidedAt: updatedApproval.decidedAt }),
            ...(updatedApproval.executionOutcome === undefined
              ? {}
              : { executionOutcome: updatedApproval.executionOutcome }),
            ...(updatedApproval.executionResult === undefined
              ? {}
              : { executionResult: updatedApproval.executionResult }),
            ...(updatedApproval.bulkExecutionAggregate === undefined
              ? {}
              : { bulkExecutionAggregate: updatedApproval.bulkExecutionAggregate }),
            ...(updatedApproval.presentation === undefined
              ? {}
              : { presentation: updatedApproval.presentation }),
          }),
        };
      }),
    );
    setPendingApprovals((current) =>
      current.map((approval) => (approval.id === updatedApproval.id ? updatedApproval : approval)),
    );
  }, []);

  const loadEarlier = useCallback(
    async (beforePrepend?: () => void): Promise<void> => {
      if (
        !historyPage?.hasMore ||
        historyPage.olderCursor === null ||
        historyControllerRef.current !== null
      )
        return;
      const controller = new AbortController();
      historyControllerRef.current = controller;
      const generation = historyGenerationRef.current;
      const options = { turnLimit: 20, olderCursor: historyPage.olderCursor };
      setIsLoadingEarlier(true);
      setEarlierHistoryError(false);
      try {
        const response = await getChatConversation(conversationId, options, controller.signal);
        if (
          !isMountedRef.current ||
          generation !== historyGenerationRef.current ||
          controller.signal.aborted
        )
          return;
        const hydrated = response.messages.map(toUiMessage);
        beforePrepend?.();
        loadedPagesRef.current.push({ options, ids: new Set(hydrated.map((item) => item.id)) });
        setHistoryPage(response.historyPage);
        setMessages((current) => {
          const merged = mergeHistoryPage(current, hydrated);
          setPendingApprovals(extractApprovalItems(merged));
          return merged;
        });
      } catch {
        if (
          isMountedRef.current &&
          generation === historyGenerationRef.current &&
          !controller.signal.aborted
        )
          setEarlierHistoryError(true);
      } finally {
        if (generation === historyGenerationRef.current) {
          historyControllerRef.current = null;
          setIsLoadingEarlier(false);
        }
      }
    },
    [conversationId, historyPage],
  );

  const refreshConversation = useCallback(async (): Promise<void> => {
    const generation = historyGenerationRef.current;
    const streamId = activeStreamIdRef.current;
    const pages = loadedPagesRef.current.filter(
      (page, index) =>
        index === 0 ||
        messagesRef.current.some((item) => page.ids.has(item.id) && messageHasLivePlan(item)),
    );
    const requests =
      pages.length === 0 ? [{ options: { turnLimit: 20 }, ids: new Set<string>() }] : pages;
    const responses = await Promise.all(
      requests.map((page) => getChatConversation(conversationId, page.options)),
    );
    if (
      !isMountedRef.current ||
      generation !== historyGenerationRef.current ||
      streamId !== activeStreamIdRef.current ||
      activeStreamControllerRef.current !== null
    )
      return;
    const latest = responses[0];
    if (latest === undefined) return;
    const hydratedMessages = responses.flatMap((response) => response.messages.map(toUiMessage));
    setMessages((current) => {
      const merged =
        latest.historyPage === undefined
          ? hydratedMessages
          : mergeHistoryPage(current, hydratedMessages);
      setPendingApprovals(extractApprovalItems(merged));
      return merged;
    });
    if (loadedPagesRef.current.length <= 1) setHistoryPage(latest.historyPage);
    requests.forEach((page, index) => {
      for (const item of responses[index]?.messages ?? []) page.ids.add(item.id);
    });
    setSelectedEntity(latest.selectedEntity ?? null);
    setSelectedDeal(latest.selectedDeal ?? null);
  }, [conversationId]);

  const decideActionPlanItem = useCallback(
    async (request: {
      planId: string;
      itemId: string;
      approvalId: string;
      decision: 'approve' | 'reject';
      remainingToken?: string;
    }): Promise<void> => {
      if (request.remainingToken)
        await approveRemainingActionPlan({
          conversationId,
          planId: request.planId,
          token: request.remainingToken,
        });
      else await decideApproval({ approvalId: request.approvalId, decision: request.decision });
      await refreshConversation();
    },
    [refreshConversation, conversationId],
  );

  const requestActionPlanRetry = useCallback(
    async (request: {
      planId: string;
      itemId: string;
      failedExecutionId: string;
    }): Promise<void> => {
      await requestActionPlanItemRetry({ conversationId, ...request });
      await refreshConversation();
    },
    [conversationId, refreshConversation],
  );

  const requestActionPlanUndo = useCallback(
    async (request: { planId: string; itemId: string }): Promise<void> => {
      await requestActionPlanItemUndo({ conversationId, ...request });
      await refreshConversation();
    },
    [conversationId, refreshConversation],
  );

  // Live plan sync (see PLAN_LIVE_POLL_MS): re-read the conversation while a
  // visible plan is non-terminal so executions that finish outside this
  // client converge without a reload. Read-only — never decides, sends or
  // writes. Skipped while streaming (the done snapshot owns the messages),
  // on hidden tabs, and once every visible plan is terminal.
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (isStreaming) return;
    if (!messages.some(messageHasLivePlan)) return;
    const timer = window.setInterval(() => {
      if (document.visibilityState !== 'visible') return;
      refreshConversation().catch(() => {
        // Transient poll failure: the next tick retries; terminal states stop
        // the poll through the messages dependency above.
      });
    }, PLAN_LIVE_POLL_MS);
    return () => window.clearInterval(timer);
  }, [messages, isStreaming, refreshConversation]);

  const reset = useCallback((): void => {
    historyControllerRef.current?.abort();
    historyControllerRef.current = null;
    historyGenerationRef.current += 1;
    loadedPagesRef.current = [];
    setHistoryPage(undefined);
    setIsLoadingEarlier(false);
    setEarlierHistoryError(false);
    setMessages([]);
    setPendingApprovals([]);
    setUsage(null);
    setSelectedEntity(null);
    setSelectedDeal(null);
  }, []);

  const cancelStream = useCallback((): void => {
    activeStreamIdRef.current += 1;
    const streamController = activeStreamControllerRef.current;
    const activeTurn = activeDurableTurnRef.current;

    activeStreamControllerRef.current = null;
    activeDurableTurnRef.current = null;
    if (activeTurn !== null) {
      clearActiveDurableChatTurn(activeTurn);
      // A local cancel does not prove the absence of a durable effect either:
      // keep the original identity so the retry replays the ledger instead of
      // re-running the agent under a fresh idempotency key.
      pendingIndeterminateTurnRef.current = activeTurn.request;
      retryableRequestRef.current = activeTurn.request;
    }
    setIsStreaming(false);
    streamController?.abort();
    if (activeTurn !== null) {
      void cancelChatTurn({
        conversationId: activeTurn.conversationId,
        runId: activeTurn.runId,
        turnId: activeTurn.turnId,
      }).catch(() => {
        // The UI remains responsive and the durable turn can be reconciled on reload/retry.
      });
    }
  }, []);

  const retryLastRequest = useCallback(async (): Promise<void> => {
    const previousRequest = retryableRequestRef.current;
    if (previousRequest === null || scope === null || activeStreamControllerRef.current !== null) {
      return;
    }

    // A retry replaces the current terminal error for this request. Keeping
    // every failed attempt made one user turn look like several independent
    // failures and left stale Retry buttons in the transcript.
    setMessages((current) => {
      let latestErrorIndex = -1;
      for (let index = current.length - 1; index >= 0; index -= 1) {
        if (current[index]?.error !== undefined) {
          latestErrorIndex = index;
          break;
        }
      }
      return latestErrorIndex === -1
        ? current
        : current.filter((_message, index) => index !== latestErrorIndex);
    });

    const indeterminateRequest = pendingIndeterminateTurnRef.current;
    pendingIndeterminateTurnRef.current = null;
    // A server-declared failure is not positive proof that a fresh identity is
    // safe, so there is no confirmed-safe branch left for failed turns: every
    // failure path marks the turn indeterminate. An indeterminate outcome is
    // therefore always retried under the SAME identity (the gateway
    // idempotency key is derived from the turn identity), which makes the
    // server replay and tail the durable ledger (`admitChatStreamTurn` +
    // `tailDurableTurn`) and report the real status without re-running the
    // agent. A fresh identity is minted only where the failure was NOT a
    // terminal turn failure (for example a user-cancelled stream).
    const turn: ActiveDurableChatTurn =
      indeterminateRequest === null
        ? (() => {
            const runId = createDurableTurnId('run');
            const turnId = createDurableTurnId('turn');
            return {
              version: 1 as const,
              workspaceId: scope.workspaceId,
              userId: scope.userId,
              conversationId,
              runId,
              turnId,
              cursor: null,
              request: { ...previousRequest, runId, turnId },
            };
          })()
        : {
            version: 1,
            workspaceId: scope.workspaceId,
            userId: scope.userId,
            conversationId,
            runId: indeterminateRequest.runId,
            turnId: indeterminateRequest.turnId,
            cursor: null,
            request: indeterminateRequest,
          };
    retryableRequestRef.current = null;
    resumedTurnKeyRef.current = `${turn.runId}:${turn.turnId}`;
    writeActiveDurableChatTurn(turn);
    // The original optimistic user message remains visible after a failed or
    // cancelled stream. Reusing it prevents a retry from duplicating the turn.
    // An indeterminate retry re-issues the SAME identity; the flag makes the
    // repeated terminal error terminate the retry loop with an explicit
    // unconfirmed-outcome report instead of an endlessly available button.
    await runDurableTurn(turn, false, {
      indeterminateResume: indeterminateRequest !== null,
    });
  }, [conversationId, runDurableTurn, scope]);

  return {
    messages,
    selectedEntity,
    selectedDeal,
    pendingApprovals: pendingApprovals.map((approval) => toUiApproval(approval, conversationId)),
    isStreaming,
    usage,
    conversationId,
    send,
    scopeReady: scope !== null,
    approve,
    applyApprovalDecision,
    decideActionPlanItem,
    requestActionPlanUndo,
    requestActionPlanRetry,
    reset,
    cancelStream,
    retryLastRequest,
    canRetryLastRequest: retryableRequestRef.current !== null && !isStreaming,
    hasEarlierMessages: historyPage?.hasMore ?? false,
    isLoadingEarlier,
    earlierHistoryError,
    loadEarlier,
  };
}
